import type { Inbox, User } from '../api.js';
import { faInt } from '../labels.js';

export type Tab =
  | 'dashboard'
  | 'visitors'
  | 'conversations'
  | 'callbacks'
  | 'catalog'
  | 'inboxes'
  | 'install';

/** Live counters shown next to the nav items, refreshed by the page that polls. */
export type NavCounts = {
  online: number;
  waiting: number;
  pendingReview: number;
};

const NAV: { key: Tab; label: string }[] = [
  { key: 'dashboard', label: 'داشبورد' },
  { key: 'visitors', label: 'آنلاین' },
  { key: 'conversations', label: 'گفتگوها' },
  { key: 'callbacks', label: 'درخواست تماس' },
  { key: 'catalog', label: 'کاتالوگ' },
  // "Inbox" is the table's name and D-021's word for a store. On screen it is
  // the store, because that is what the person clicking it is thinking about.
  { key: 'inboxes', label: 'فروشگاه‌ها' },
  { key: 'install', label: 'نصب ویجت' },
];

/** Two initials, so a person is recognisable at 30px without a photo. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '؟';
  if (parts.length === 1) return parts[0]!.slice(0, 2);
  return `${parts[0]![0]}.${parts[1]![0]}`;
}

function Badge({ tab, counts, active }: { tab: Tab; counts: NavCounts; active: boolean }) {
  if (tab === 'visitors') {
    // Zero people on the site is not an alert, so it loses its colour rather
    // than shouting a green nought at an empty shop.
    return (
      <span
        className={`flex items-center gap-1.5 text-xs font-medium ${
          counts.online > 0 ? 'text-ok' : 'text-ink/35'
        }`}
      >
        {counts.online > 0 && <span className="h-1.5 w-1.5 rounded-full bg-ok" />}
        {faInt(counts.online)}
      </span>
    );
  }

  if (tab === 'conversations' && counts.waiting > 0) {
    return (
      <span
        className={`rounded-full px-2 py-0.5 text-2xs font-bold ${
          active ? 'bg-white text-ink-strong' : 'bg-brand text-white'
        }`}
      >
        {faInt(counts.waiting)}
      </span>
    );
  }

  if (tab === 'catalog' && counts.pendingReview > 0) {
    return (
      <span
        className={`rounded-full text-2xs font-medium ${
          active ? 'text-white/65' : 'bg-warn-bg px-2 py-0.5 text-warn-text'
        }`}
      >
        {faInt(counts.pendingReview)}
      </span>
    );
  }

  return null;
}

export function Sidebar({
  tab,
  onTab,
  inboxes,
  activeInbox,
  onInbox,
  counts,
  user,
  onSignOut,
}: {
  tab: Tab;
  onTab: (t: Tab) => void;
  inboxes: Inbox[];
  activeInbox: string;
  onInbox: (id: string) => void;
  counts: NavCounts;
  user: User;
  onSignOut: () => void;
}) {
  const box = inboxes.find((i) => i.id === activeInbox);

  return (
    <div className="flex h-full w-[280px] shrink-0 flex-col bg-paper py-5 md:w-[222px] md:border-e md:border-ink/[.08]">
      <div className="flex items-center gap-2.5 px-5 pb-5">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-ink-strong font-mono text-sm font-bold text-white">
          S
        </span>
        <span className="font-mono text-xs font-bold tracking-[.2em]" dir="ltr">
          SKINBOT
        </span>
      </div>

      {/*
        The store selector, and the widget's on/off state in the same box.
        Which shop you are looking at and whether its widget is live are the two
        things that make every other number on screen mean something different.
      */}
      <div className="mb-2 border-b border-ink/[.07] px-3.5 pb-3.5">
        <label className="relative flex cursor-pointer items-center justify-between rounded-lg border border-ink/10 bg-white px-3 py-2.5">
          <span className="truncate text-sm font-medium">{box?.name ?? '—'}</span>
          <span className="flex shrink-0 items-center gap-1.5">
            <span
              className={`h-1.5 w-1.5 rounded-full ${box?.isActive ? 'bg-ok' : 'bg-ink/25'}`}
            />
            <span className="text-2xs text-ink/45">
              {box?.isActive ? 'ویجت روشن' : 'خاموش'}
            </span>
          </span>
          <select
            className="absolute inset-0 cursor-pointer opacity-0"
            value={activeInbox}
            onChange={(e) => onInbox(e.target.value)}
            aria-label="انتخاب فروشگاه"
          >
            {inboxes.map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <nav className="flex flex-col gap-0.5 px-3.5">
        {NAV.map(({ key, label }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              onClick={() => onTab(key)}
              className={`flex items-center justify-between rounded-lg px-3 py-2.5 text-start text-base transition ${
                active
                  ? 'bg-ink-strong font-bold text-white'
                  : 'text-ink/65 hover:bg-ink/[.04] hover:text-ink'
              }`}
            >
              <span>{label}</span>
              <Badge tab={key} counts={counts} active={active} />
            </button>
          );
        })}
      </nav>

      <div className="mt-auto flex items-center gap-2.5 border-t border-ink/[.07] px-4 pt-3.5">
        <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-full bg-brand-100 text-2xs font-bold text-brand">
          {initials(user.displayName ?? user.email)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium">
            {user.displayName ?? user.email}
          </div>
          <div className="text-2xs text-ink/45">
            {user.role === 'admin' ? 'مدیر' : 'اپراتور'}
          </div>
        </div>
        <button
          onClick={onSignOut}
          className="shrink-0 text-2xs text-ink/40 transition hover:text-bad"
        >
          خروج
        </button>
      </div>
    </div>
  );
}
